import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Wrench,
  Play,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Clock,
  FlaskConical,
  Calculator,
  Download,
  Table2,
  Upload,
  FileCode,
  FileJson,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { runAllSimulatorsTests } from "@/lib/api/test-scenarios";
import { listSimulators, getSimulator } from "@/lib/api/simulators";
import { listAllReferenceTables } from "@/lib/api/references";
import { createTestScenario } from "@/lib/api/test-scenarios";
import { exportVersion, ExportVersionStats } from "@/lib/api/admin";
import { getErrorMessage } from "@/lib/api/client";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import type { AllSimulatorsTestResult, SimulatorTestResult, TestScenarioRunResult } from "@/types";

type SortMode = "default" | "name" | "max_percent_diff";

function getMaxAbsPercentDiff(result: TestScenarioRunResult): number {
  let maxDiff = 0;
  for (const r of Object.values(result.results)) {
    const expected = typeof r.expected === "number" ? r.expected : null;
    const actual = typeof r.actual === "number" ? r.actual : null;
    if (expected !== null && actual !== null && expected !== 0) {
      const pct = Math.abs(((actual - expected) / Math.abs(expected)) * 100);
      if (pct > maxDiff) maxDiff = pct;
    }
  }
  return maxDiff;
}

function getSimulatorMaxAbsPercentDiff(sim: SimulatorTestResult): number {
  let maxDiff = 0;
  for (const result of sim.results) {
    const diff = getMaxAbsPercentDiff(result);
    if (diff > maxDiff) maxDiff = diff;
  }
  return maxDiff;
}

export const Route = createFileRoute("/_dashboard/admin/utilities")({
  component: AdminUtilitiesPage,
});

function AdminUtilitiesPage() {
  const [testResults, setTestResults] = useState<AllSimulatorsTestResult | null>(null);
  const [expandedSimulators, setExpandedSimulators] = useState<Set<number>>(new Set());
  const [sortMode, setSortMode] = useState<SortMode>("default");

  const runAllTestsMutation = useMutation({
    mutationFn: runAllSimulatorsTests,
    onSuccess: (result) => {
      setTestResults(result);
      
      // Show appropriate toast based on results
      if (result.total_scenarios === 0) {
        toast.info("Aucun test a executer", {
          description: `${result.total_simulators} simulateur(s) actif(s), mais aucun n'a de scenarios de test.`,
        });
      } else if (result.total_failed === 0 && result.total_errors === 0) {
        toast.success("Tous les tests ont reussi", {
          description: `${result.total_passed}/${result.total_scenarios} test(s) reussi(s).`,
        });
      } else {
        toast.warning("Certains tests ont echoue", {
          description: `${result.total_passed} reussi(s), ${result.total_failed} echoue(s), ${result.total_errors} erreur(s).`,
        });
      }
    },
    onError: (error) => {
      toast.error("Erreur lors de l'execution des tests", {
        description: getErrorMessage(error),
      });
    },
  });

  const toggleSimulatorExpanded = (simulatorId: number) => {
    setExpandedSimulators((prev) => {
      const next = new Set(prev);
      if (next.has(simulatorId)) {
        next.delete(simulatorId);
      } else {
        next.add(simulatorId);
      }
      return next;
    });
  };

  const getStatusBadge = (result: SimulatorTestResult) => {
    if (result.errors > 0) {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          {result.errors} erreur(s)
        </Badge>
      );
    }
    if (result.failed > 0) {
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          {result.failed} echoue(s)
        </Badge>
      );
    }
    return (
      <Badge variant="default" className="gap-1 bg-green-600 hover:bg-green-700">
        <CheckCircle2 className="h-3 w-3" />
        {result.passed} reussi(s)
      </Badge>
    );
  };

  const sortedSimulators = useMemo(() => {
    if (!testResults) return [];
    if (sortMode === "default") return testResults.simulators;
    const sorted = [...testResults.simulators];
    if (sortMode === "name") {
      sorted.sort((a, b) => a.simulator_name.localeCompare(b.simulator_name, "fr"));
      for (const sim of sorted) {
        sim.results = [...sim.results].sort((a, b) =>
          a.scenario_name.localeCompare(b.scenario_name, "fr")
        );
      }
    } else if (sortMode === "max_percent_diff") {
      sorted.sort((a, b) => getSimulatorMaxAbsPercentDiff(b) - getSimulatorMaxAbsPercentDiff(a));
      for (const sim of sorted) {
        sim.results = [...sim.results].sort(
          (a, b) => getMaxAbsPercentDiff(b) - getMaxAbsPercentDiff(a)
        );
      }
    }
    return sorted;
  }, [testResults, sortMode]);

  return (
    <AdminGuard>
      <div className="flex flex-col h-full">
        <Header title="Utilitaires" />

        <div className="flex-1 p-6 space-y-6 overflow-auto">
          {/* Page description */}
          <div className="max-w-3xl">
            <p className="text-muted-foreground">
              Cette page regroupe des outils utilitaires pour la gestion et le diagnostic des simulateurs.
            </p>
          </div>

          {/* Tools grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Run All Tests Tool */}
            <Card className="md:col-span-2 lg:col-span-3">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FlaskConical className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Lancer tous les tests</CardTitle>
                      <CardDescription>
                        Execute tous les scenarios de test pour la derniere version publiee de chaque simulateur actif.
                      </CardDescription>
                    </div>
                  </div>
                  <Button
                    onClick={() => runAllTestsMutation.mutate()}
                    disabled={runAllTestsMutation.isPending}
                    className="shrink-0"
                  >
                    {runAllTestsMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Execution...
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Lancer les tests
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>

              {/* Results section */}
              {testResults && (
                <CardContent className="space-y-4">
                  {/* Summary */}
                  <div className="p-4 rounded-lg bg-muted/50 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Resume global</span>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {testResults.execution_time_ms}ms
                      </div>
                    </div>

                    {testResults.total_scenarios > 0 ? (
                      <>
                        <Progress
                          value={(testResults.total_passed / testResults.total_scenarios) * 100}
                          className="h-2"
                        />
                        <div className="flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded-full bg-green-500" />
                            <span>{testResults.total_passed} reussi(s)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded-full bg-red-500" />
                            <span>{testResults.total_failed} echoue(s)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded-full bg-yellow-500" />
                            <span>{testResults.total_errors} erreur(s)</span>
                          </div>
                          <div className="text-muted-foreground">
                            {testResults.simulators_with_tests}/{testResults.total_simulators} simulateur(s) avec tests
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {testResults.total_simulators} simulateur(s) actif(s), mais aucun n'a de scenarios de test configures.
                      </p>
                    )}
                  </div>

                  {/* Sort control */}
                  {testResults.simulators.length > 1 && (
                    <div className="flex items-center gap-2">
                      <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                      <Select value={sortMode} onValueChange={(v) => setSortMode(v as SortMode)}>
                        <SelectTrigger className="w-[220px] h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Ordre par défaut</SelectItem>
                          <SelectItem value="name">Nom (A-Z)</SelectItem>
                          <SelectItem value="max_percent_diff">Plus gros écart %</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Per-simulator results */}
                  {sortedSimulators.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground">Resultats par simulateur</h4>
                      <div className="space-y-2">
                        {sortedSimulators.map((sim) => (
                          <Collapsible
                            key={sim.simulator_id}
                            open={expandedSimulators.has(sim.simulator_id)}
                            onOpenChange={() => toggleSimulatorExpanded(sim.simulator_id)}
                          >
                            <div className="border rounded-lg">
                              <CollapsibleTrigger asChild>
                                <button className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left">
                                  <div className="flex items-center gap-3">
                                    {expandedSimulators.has(sim.simulator_id) ? (
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                    <div>
                                      <span className="font-medium">{sim.simulator_name}</span>
                                      <span className="text-sm text-muted-foreground ml-2">
                                        v{sim.version_number}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm text-muted-foreground">
                                      {sim.total} test(s)
                                    </span>
                                    {getStatusBadge(sim)}
                                  </div>
                                </button>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="border-t p-3 space-y-2 bg-muted/25">
                                  {sim.results.map((scenario) => (
                                    <div
                                      key={scenario.scenario_id}
                                      className="flex items-center justify-between py-2 px-3 rounded bg-background"
                                    >
                                      <span className="text-sm">{scenario.scenario_name}</span>
                                      <div className="flex items-center gap-2">
                                        {(() => {
                                          const maxDiff = getMaxAbsPercentDiff(scenario);
                                          return maxDiff > 0 ? (
                                            <span className="text-xs text-muted-foreground font-mono">
                                              {maxDiff.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                                            </span>
                                          ) : null;
                                        })()}
                                        {scenario.errors.length > 0 ? (
                                          <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                                            <AlertCircle className="h-3 w-3 mr-1" />
                                            Erreur
                                          </Badge>
                                        ) : scenario.passed ? (
                                          <Badge variant="outline" className="text-green-600 border-green-600">
                                            <CheckCircle2 className="h-3 w-3 mr-1" />
                                            OK
                                          </Badge>
                                        ) : (
                                          <Badge variant="outline" className="text-red-600 border-red-600">
                                            <XCircle className="h-3 w-3 mr-1" />
                                            Echoue
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </CollapsibleContent>
                            </div>
                          </Collapsible>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Export Version Tool */}
            <ExportVersionCard />

            {/* Import Test Scenarios Tool */}
            <TestScenariosImportCard />
          </div>

          {/* Placeholder for future tools */}
          <div className="text-center py-8 text-muted-foreground">
            <Wrench className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">D'autres outils seront ajoutes prochainement.</p>
          </div>
        </div>
      </div>
    </AdminGuard>
  );
}

// ============== Export Version Card ==============

function ExportVersionCard() {
  const [isExportingVersion, setIsExportingVersion] = useState(false);
  const [selectedSimulatorId, setSelectedSimulatorId] = useState<string>("");
  const [selectedVersionId, setSelectedVersionId] = useState<string>("");
  const [additionalTableIds, setAdditionalTableIds] = useState<number[]>([]);
  const [includeTestScenarios, setIncludeTestScenarios] = useState(false);
  const [lastVersionExport, setLastVersionExport] = useState<{
    exportedAt: string;
    simulatorName: string;
    versionNumber: number;
    stats: ExportVersionStats;
  } | null>(null);

  // Fetch simulators list (include unpublished to allow exporting draft versions)
  const { data: simulatorsData, isLoading: loadingSimulators } = useQuery({
    queryKey: ["simulators-for-export"],
    queryFn: () => listSimulators({ page_size: 100, include_unpublished: true }),
  });

  // Fetch selected simulator's versions
  const { data: simulatorWithVersions, isLoading: loadingVersions } = useQuery({
    queryKey: ["simulator-versions-for-export", selectedSimulatorId],
    queryFn: () => getSimulator(parseInt(selectedSimulatorId)),
    enabled: !!selectedSimulatorId,
  });

  // Fetch all reference tables for additional selection
  const { data: referenceTablesData, isLoading: loadingReferenceTables, error: referenceTablesError } = useQuery({
    queryKey: ["reference-tables-for-export"],
    queryFn: () => listAllReferenceTables(),
  });

  const handleExportVersion = async () => {
    if (!selectedVersionId) {
      toast.error("Veuillez sélectionner une version");
      return;
    }
    
    setIsExportingVersion(true);
    try {
      const result = await exportVersion(
        parseInt(selectedVersionId),
        additionalTableIds.length > 0 ? additionalTableIds : undefined,
        includeTestScenarios
      );
      
      // Créer le fichier JSON et le télécharger
      const jsonString = JSON.stringify(result, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `${result.simulator.slug}-v${result.version.version_number}-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setLastVersionExport({
        exportedAt: result.exported_at,
        simulatorName: result.simulator.name,
        versionNumber: result.version.version_number,
        stats: result.stats,
      });
      
      const testScenariosInfo = result.stats.test_scenarios_count 
        ? `, ${result.stats.test_scenarios_count} test(s)` 
        : "";
      toast.success("Export de version réussi", {
        description: `${result.simulator.name} v${result.version.version_number} avec ${result.stats.reference_tables_count} table(s) de référence${testScenariosInfo}`,
      });
    } catch (error) {
      toast.error("Erreur", { description: getErrorMessage(error) });
    } finally {
      setIsExportingVersion(false);
    }
  };

  return (
    <Card className="md:col-span-2 lg:col-span-3 border-primary/20">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Calculator className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Export d'une version de simulateur</CardTitle>
            <CardDescription>
              Exportez une version spécifique avec ses tables de référence utilisées
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Sélection Simulateur */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Simulateur</Label>
            <Select
              value={selectedSimulatorId}
              onValueChange={(value) => {
                setSelectedSimulatorId(value);
                setSelectedVersionId("");
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingSimulators ? "Chargement..." : "Sélectionnez un simulateur"} />
              </SelectTrigger>
              <SelectContent>
                {simulatorsData?.items.map((sim) => (
                  <SelectItem key={sim.id} value={sim.id.toString()}>
                    {sim.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sélection Version */}
          {selectedSimulatorId && (
            <div className="space-y-2">
              <Label>Version</Label>
              <Select
                value={selectedVersionId}
                onValueChange={(value) => {
                  setSelectedVersionId(value);
                  setAdditionalTableIds([]);
                  setIncludeTestScenarios(false);
                }}
                disabled={loadingVersions}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingVersions ? "Chargement..." : "Sélectionnez une version"} />
                </SelectTrigger>
                <SelectContent>
                  {simulatorWithVersions?.versions.map((version) => (
                    <SelectItem key={version.id} value={version.id.toString()}>
                      Version {version.version_number} ({version.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Sélection Tables de Référence Supplémentaires */}
          {selectedVersionId && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Table2 className="h-4 w-4" />
                Tables de référence supplémentaires (optionnel)
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Ajoutez des tables de référence qui ne sont pas encore utilisées par la version
              </p>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-2 bg-muted/30">
                {loadingReferenceTables ? (
                  <p className="text-sm text-muted-foreground">Chargement des tables...</p>
                ) : referenceTablesError ? (
                  <p className="text-sm text-error">Erreur: {getErrorMessage(referenceTablesError)}</p>
                ) : referenceTablesData?.items && referenceTablesData.items.length > 0 ? (
                  referenceTablesData.items.map((table) => (
                    <label
                      key={table.id}
                      className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={additionalTableIds.includes(table.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAdditionalTableIds([...additionalTableIds, table.id]);
                          } else {
                            setAdditionalTableIds(additionalTableIds.filter((id) => id !== table.id));
                          }
                        }}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      />
                      <span className="text-sm">{table.name}</span>
                      <span className="text-xs text-muted-foreground">({table.slug})</span>
                    </label>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">Aucune table de référence disponible</p>
                )}
              </div>
              {additionalTableIds.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {additionalTableIds.length} table(s) supplémentaire(s) sélectionnée(s)
                </p>
              )}
            </div>
          )}

          {/* Option Scénarios de Test */}
          {selectedVersionId && (
            <div className="space-y-2">
              <label
                className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={includeTestScenarios}
                  onChange={(e) => setIncludeTestScenarios(e.target.checked)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                <div>
                  <span className="text-sm font-medium">Inclure les scénarios de test</span>
                  <p className="text-xs text-muted-foreground">
                    Exporte les tests avec leurs inputs et outputs attendus
                  </p>
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="p-4 rounded-lg bg-muted/50 space-y-2">
          <p className="text-sm font-medium">L'export de version inclut :</p>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>Variables et groupes de variables</li>
            <li>Champs de formulaire et groupes (sans apparence/layout)</li>
            <li>Champs de sortie et formules</li>
            <li>Tables de référence utilisées + tables supplémentaires sélectionnées</li>
            {includeTestScenarios && <li className="text-primary">Scénarios de test (inputs et outputs attendus)</li>}
          </ul>
          <p className="text-xs text-warning mt-2">
            ⚠️ Les paramètres d'apparence (largeur, layout, icônes) ne sont pas exportés.
          </p>
        </div>

        {/* Dernier export de version */}
        {lastVersionExport && (
          <div className="p-4 rounded-lg bg-success/10 border border-success/20">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <p className="font-medium text-sm">Dernier export réussi</p>
            </div>
            <p className="text-xs text-muted-foreground">
              {lastVersionExport.simulatorName} v{lastVersionExport.versionNumber} • 
              {lastVersionExport.stats.form_fields_count} champ(s), 
              {lastVersionExport.stats.output_fields_count} sortie(s), 
              {lastVersionExport.stats.reference_tables_count} table(s)
              {lastVersionExport.stats.test_scenarios_count > 0 && (
                <>, {lastVersionExport.stats.test_scenarios_count} test(s)</>
              )}
            </p>
          </div>
        )}

        {/* Bouton d'export version */}
        <Button
          onClick={handleExportVersion}
          disabled={isExportingVersion || !selectedVersionId}
          size="lg"
          className="w-full"
        >
          {isExportingVersion ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Export en cours...
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" />
              Exporter cette version
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// ============== Test Scenarios Import Card ==============

interface TestScenarioImportData {
  name: string;
  description?: string;
  inputs: Record<string, unknown>;
  expected_outputs: Record<string, { value: unknown; tolerance?: number; tolerance_type?: "percentage" | "absolute" } | unknown>;
}

interface TestScenarioImportFile {
  test_scenarios: TestScenarioImportData[];
}

function TestScenariosImportCard() {
  const queryClient = useQueryClient();
  const [selectedSimulatorId, setSelectedSimulatorId] = useState<string>("");
  const [selectedVersionId, setSelectedVersionId] = useState<string>("");
  const [importPreview, setImportPreview] = useState<TestScenarioImportFile | null>(null);
  const [importProgress, setImportProgress] = useState<{
    total: number;
    completed: number;
    errors: string[];
  } | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importMode, setImportMode] = useState<"file" | "paste">("paste");
  const [jsonText, setJsonText] = useState("");

  // Fetch simulators
  const { data: simulatorsData } = useQuery({
    queryKey: ["simulators"],
    queryFn: () => listSimulators({ page: 1, page_size: 100 }),
  });

  // Fetch selected simulator versions
  const { data: selectedSimulator } = useQuery({
    queryKey: ["simulator", selectedSimulatorId],
    queryFn: () => getSimulator(parseInt(selectedSimulatorId)),
    enabled: !!selectedSimulatorId,
  });

  const simulators = simulatorsData?.items || [];
  const versions = selectedSimulator?.versions || [];

  // Validate and set import preview from parsed JSON
  const validateAndSetPreview = (jsonString: string): boolean => {
    try {
      const json = JSON.parse(jsonString) as TestScenarioImportFile;

      if (!json.test_scenarios || !Array.isArray(json.test_scenarios)) {
        toast.error("Format invalide", {
          description: "Le JSON doit contenir un tableau 'test_scenarios'",
        });
        return false;
      }

      if (json.test_scenarios.length === 0) {
        toast.error("Format invalide", {
          description: "Le tableau 'test_scenarios' est vide",
        });
        return false;
      }

      // Validate each scenario
      for (const scenario of json.test_scenarios) {
        if (!scenario.name) {
          toast.error("Format invalide", {
            description: "Chaque scénario doit avoir un nom",
          });
          return false;
        }
      }

      setImportPreview(json);
      setImportProgress(null);
      return true;
    } catch {
      toast.error("Erreur de parsing", {
        description: "Le contenu n'est pas un JSON valide",
      });
      return false;
    }
  };

  // Handle file select
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      validateAndSetPreview(e.target?.result as string);
    };
    reader.readAsText(file);
  };

  // Handle JSON paste validation
  const handleJsonPaste = () => {
    if (!jsonText.trim()) {
      toast.error("JSON vide", {
        description: "Collez du JSON dans le champ ci-dessus",
      });
      return;
    }
    validateAndSetPreview(jsonText);
  };

  // Handle import
  const handleImport = async () => {
    if (!importPreview || !selectedVersionId) return;

    setIsImporting(true);
    const versionId = parseInt(selectedVersionId);
    const errors: string[] = [];
    let completed = 0;

    setImportProgress({
      total: importPreview.test_scenarios.length,
      completed: 0,
      errors: [],
    });

    for (const scenario of importPreview.test_scenarios) {
      try {
        await createTestScenario(versionId, {
          name: scenario.name,
          description: scenario.description || null,
          inputs: scenario.inputs || {},
          expected_outputs: scenario.expected_outputs || {},
        });
        completed++;
      } catch (error) {
        errors.push(`Erreur pour "${scenario.name}": ${getErrorMessage(error)}`);
      }
      
      setImportProgress({
        total: importPreview.test_scenarios.length,
        completed,
        errors,
      });
    }

    setIsImporting(false);
    
    if (errors.length === 0) {
      toast.success("Import réussi", {
        description: `${completed} scénario(s) importé(s)`,
      });
      // Reset
      setImportPreview(null);
      setJsonText("");
      // Invalidate test scenarios query
      queryClient.invalidateQueries({ queryKey: ["test-scenarios", versionId] });
    } else {
      toast.error("Import partiel", {
        description: `${completed} réussi(s), ${errors.length} erreur(s)`,
      });
    }
  };

  const cancelImport = () => {
    setImportPreview(null);
    setImportProgress(null);
    setJsonText("");
  };

  return (
    <Card className="md:col-span-2 lg:col-span-3">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <FlaskConical className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Import de scénarios de test</CardTitle>
            <CardDescription>
              Importez des scénarios de test depuis un fichier JSON ou en collant du JSON
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Simulator & Version Selector */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Simulateur</Label>
            <Select
              value={selectedSimulatorId}
              onValueChange={(v) => {
                setSelectedSimulatorId(v);
                setSelectedVersionId("");
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un simulateur" />
              </SelectTrigger>
              <SelectContent>
                {simulators.map((sim) => (
                  <SelectItem key={sim.id} value={String(sim.id)}>
                    {sim.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Version</Label>
            <Select
              value={selectedVersionId}
              onValueChange={setSelectedVersionId}
              disabled={!selectedSimulatorId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une version" />
              </SelectTrigger>
              <SelectContent>
                {versions.map((ver) => (
                  <SelectItem key={ver.id} value={String(ver.id)}>
                    v{ver.version_number} ({ver.status})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator />

        {/* Import Source: File or Paste */}
        {!importPreview ? (
          <div className="space-y-4">
            <Tabs value={importMode} onValueChange={(v) => setImportMode(v as "file" | "paste")}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="paste">
                  <FileCode className="mr-2 h-4 w-4" />
                  Coller du JSON
                </TabsTrigger>
                <TabsTrigger value="file">
                  <Upload className="mr-2 h-4 w-4" />
                  Fichier
                </TabsTrigger>
              </TabsList>

              <TabsContent value="paste" className="space-y-3 mt-3">
                <Label htmlFor="test-scenarios-json">JSON à importer</Label>
                <Textarea
                  id="test-scenarios-json"
                  placeholder={'{\n  "test_scenarios": [\n    {\n      "name": "Mon scénario",\n      "inputs": { ... },\n      "expected_outputs": { ... }\n    }\n  ]\n}'}
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  className="font-mono text-xs min-h-[200px]"
                  disabled={!selectedVersionId}
                />
                <Button
                  onClick={handleJsonPaste}
                  className="w-full"
                  disabled={!selectedVersionId || !jsonText.trim()}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Valider le JSON
                </Button>
              </TabsContent>

              <TabsContent value="file" className="space-y-3 mt-3">
                <Label htmlFor="test-scenarios-file">Fichier JSON à importer</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="test-scenarios-file"
                    type="file"
                    accept=".json,application/json"
                    onChange={handleFileSelect}
                    className="cursor-pointer"
                    disabled={!selectedVersionId}
                  />
                </div>
              </TabsContent>
            </Tabs>

            {!selectedVersionId && (
              <p className="text-sm text-muted-foreground">
                Sélectionnez d'abord un simulateur et une version
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Preview */}
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
              <div className="flex items-center gap-2 mb-3">
                <FileJson className="h-4 w-4 text-primary" />
                <p className="font-medium text-sm">Fichier prêt à importer</p>
              </div>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="text-muted-foreground">Nombre de scénarios :</span>{" "}
                  <strong>{importPreview.test_scenarios.length}</strong>
                </p>
                <Separator className="my-2" />
                <p className="font-medium">Scénarios :</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 max-h-40 overflow-auto">
                  {importPreview.test_scenarios.map((scenario, i) => (
                    <li key={i}>
                      <strong>{scenario.name}</strong>
                      {" - "}
                      {Object.keys(scenario.inputs || {}).length} entrée(s),{" "}
                      {Object.keys(scenario.expected_outputs || {}).length} sortie(s)
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Progress */}
            {importProgress && (
              <div className="p-4 rounded-lg bg-muted/50 border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    Import en cours...
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {importProgress.completed}/{importProgress.total}
                  </span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${(importProgress.completed / importProgress.total) * 100}%` }}
                  />
                </div>
                {importProgress.errors.length > 0 && (
                  <div className="mt-3 text-xs text-error space-y-1">
                    {importProgress.errors.map((err, i) => (
                      <p key={i}>{err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={cancelImport}
                className="flex-1"
                disabled={isImporting}
              >
                Annuler
              </Button>
              <Button
                onClick={handleImport}
                className="flex-1"
                disabled={isImporting || !selectedVersionId}
              >
                {isImporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Importer {importPreview.test_scenarios.length} scénario(s)
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Format info */}
        <div className="p-4 rounded-lg bg-muted/30 border">
          <p className="font-medium text-sm mb-2">Format attendu :</p>
          <pre className="text-xs text-muted-foreground overflow-auto max-h-32 bg-muted/50 p-2 rounded">
{`{
  "test_scenarios": [
    {
      "name": "Nom du scénario",
      "description": "Description optionnelle",
      "inputs": {
        "field_slug": "valeur",
        "number_field": 123
      },
      "expected_outputs": {
        "output_slug": {
          "value": 12450,
          "tolerance": 0.01,
          "tolerance_type": "percentage"
        }
      }
    }
  ]
}`}
          </pre>
        </div>
      </CardContent>
    </Card>
  );
}
